<script setup lang="ts">
import { computed, ref } from 'vue'
import UiButton from '../UiButton.vue'
import UiCheckbox from '../UiCheckbox.vue'
import UiDialog from '../UiDialog.vue'
import UiIcon from '../UiIcon.vue'
import UiSelect from '../UiSelect.vue'
import { MAX_SORT, moveKey } from './tableState'
import type { DataTableColumn, SortCriterion } from './types'

// "Colunas e ordenação" of a UiDataTable: the sort criteria (add, flip, reorder, remove) and the
// columns (show/hide, drag or arrows to reorder). Every change applies at once.
const props = defineProps<{
  open: boolean
  title?: string
  columns: DataTableColumn<never>[]
  hidden: ReadonlySet<string>
  sort: SortCriterion[]
}>()

const emit = defineEmits<{
  close: []
  layout: [order: string[], hidden: string[]]
  sort: [sort: SortCriterion[]]
  reset: []
}>()

const sortable = computed(() => props.columns.filter((column) => column.sortable !== false))
const labelOf = (key: string) => props.columns.find((column) => column.key === key)?.label ?? key
const visibleCount = computed(() => props.columns.filter((column) => !props.hidden.has(column.key)).length)
const directions = [
  { value: 'asc' as const, label: 'Crescente (A→Z, 0→9)' },
  { value: 'desc' as const, label: 'Decrescente (Z→A, 9→0)' },
]

function columnOptions(current: string) {
  return sortable.value
    .filter((column) => column.key === current || !props.sort.some((criterion) => criterion.key === column.key))
    .map((column) => ({ value: column.key, label: column.label }))
}

function setSort(index: number, change: Partial<SortCriterion>) {
  emit('sort', props.sort.map((criterion, i) => (i === index ? { ...criterion, ...change } : criterion)))
}
function moveSort(index: number, delta: number) {
  const next = [...props.sort]
  const [item] = next.splice(index, 1)
  if (!item) return
  next.splice(index + delta, 0, item)
  emit('sort', next)
}
function addSort() {
  const free = sortable.value.find((column) => !props.sort.some((criterion) => criterion.key === column.key))
  if (free) emit('sort', [...props.sort, { key: free.key, desc: false }])
}

const order = computed(() => props.columns.map((column) => column.key))
function setVisible(key: string, visible: boolean) {
  const hidden = new Set(props.hidden)
  if (visible) hidden.delete(key)
  else hidden.add(key)
  emit('layout', order.value, [...hidden])
}
function moveColumn(key: string, toIndex: number) {
  emit('layout', moveKey(order.value, key, toIndex), [...props.hidden])
}

// Drag and drop of the column list (the arrows do the same from the keyboard).
const dragging = ref<string | null>(null)
const over = ref<string | null>(null)
function onDrop(target: string) {
  if (dragging.value && dragging.value !== target) moveColumn(dragging.value, order.value.indexOf(target))
  dragging.value = over.value = null
}
</script>

<template>
  <UiDialog :open="open" :title="title ?? 'Colunas e ordenação'" size="medium" close-label="Fechar" @close="emit('close')">
    <div class="settings">
      <section class="settings__section" aria-labelledby="settings-sort">
        <h3 id="settings-sort" class="settings__title">Ordenação</h3>
        <p class="settings__hint">Clique no título de uma coluna para ordenar por ela. Com Shift + clique, ela entra como próximo critério.</p>
        <p v-if="!sort.length" class="settings__empty">Sem ordenação: os registros aparecem na ordem padrão.</p>
        <ol v-else class="settings__list">
          <li v-for="(criterion, index) in sort" :key="criterion.key" class="settings__sort">
            <span class="settings__rank" aria-hidden="true">{{ index + 1 }}</span>
            <UiSelect
              :model-value="criterion.key"
              :label="index === 0 ? 'Ordenar por' : 'Depois por'"
              :options="columnOptions(criterion.key)"
              compact
              @update:model-value="(key: string) => setSort(index, { key })"
            />
            <UiSelect
              :model-value="criterion.desc ? 'desc' : 'asc'"
              label="Direção"
              :options="directions"
              compact
              @update:model-value="(value: 'asc' | 'desc') => setSort(index, { desc: value === 'desc' })"
            />
            <span class="settings__buttons">
              <button type="button" class="settings__icon" :disabled="index === 0" :aria-label="`Subir ${labelOf(criterion.key)} na ordenação`" @click="moveSort(index, -1)">
                <UiIcon name="arrow-up" :size="15" />
              </button>
              <button type="button" class="settings__icon" :disabled="index === sort.length - 1" :aria-label="`Descer ${labelOf(criterion.key)} na ordenação`" @click="moveSort(index, 1)">
                <UiIcon name="arrow-down" :size="15" />
              </button>
              <button type="button" class="settings__icon" :aria-label="`Tirar ${labelOf(criterion.key)} da ordenação`" @click="emit('sort', sort.filter((_, i) => i !== index))">
                <UiIcon name="x" :size="15" />
              </button>
            </span>
          </li>
        </ol>
        <div class="settings__actions">
          <UiButton variant="gray" size="small" :disabled="sort.length >= Math.min(MAX_SORT, sortable.length)" @click="addSort">
            <template #icon><UiIcon name="plus" :size="15" /></template>
            Adicionar ordenação
          </UiButton>
          <UiButton v-if="sort.length" variant="plain" size="small" @click="emit('sort', [])">Limpar ordenação</UiButton>
        </div>
      </section>

      <section class="settings__section" aria-labelledby="settings-columns">
        <h3 id="settings-columns" class="settings__title">Colunas</h3>
        <p class="settings__hint">Desmarque as que você não usa e arraste (ou use as setas) para mudar a ordem. Fica salvo para você.</p>
        <ul class="settings__list">
          <li
            v-for="(column, index) in columns"
            :key="column.key"
            class="settings__column"
            :class="{ 'settings__column--over': over === column.key && dragging !== column.key, 'settings__column--dragging': dragging === column.key }"
            draggable="true"
            @dragstart="dragging = column.key"
            @dragend="dragging = over = null"
            @dragover.prevent="over = column.key"
            @drop.prevent="onDrop(column.key)"
          >
            <span class="settings__grip" aria-hidden="true"><UiIcon name="grip" :size="16" /></span>
            <UiCheckbox
              :model-value="!hidden.has(column.key)"
              :label="column.label"
              :disabled="column.hideable === false || (!hidden.has(column.key) && visibleCount <= 1)"
              @update:model-value="(visible: boolean) => setVisible(column.key, visible)"
            />
            <span class="settings__buttons">
              <button type="button" class="settings__icon" :disabled="index === 0" :aria-label="`Mover ${column.label} para cima`" @click="moveColumn(column.key, index - 1)">
                <UiIcon name="arrow-up" :size="15" />
              </button>
              <button type="button" class="settings__icon" :disabled="index === columns.length - 1" :aria-label="`Mover ${column.label} para baixo`" @click="moveColumn(column.key, index + 1)">
                <UiIcon name="arrow-down" :size="15" />
              </button>
            </span>
          </li>
        </ul>
      </section>
    </div>

    <template #footer>
      <UiButton variant="plain" @click="emit('reset')">Restaurar padrão</UiButton>
      <UiButton @click="emit('close')">Concluir</UiButton>
    </template>
  </UiDialog>
</template>

<style scoped>
.settings {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.settings__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.settings__title {
  margin: 0;
  font-size: var(--type-overline-size);
  letter-spacing: var(--type-overline-tracking);
  text-transform: uppercase;
  color: var(--tertiary-label);
  font-weight: var(--weight-semibold);
}
.settings__hint,
.settings__empty {
  margin: 0;
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.settings__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.settings__sort {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: end;
  gap: 8px;
}
.settings__rank {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--weight-semibold);
  color: var(--text-secondary);
}
.settings__column {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: var(--hairline) solid var(--separator);
  border-radius: var(--radius-md);
  background: var(--surface-card);
}
.settings__column :deep(.ui-check) {
  flex: 1;
  min-width: 0;
}
.settings__column--over {
  border-color: var(--accent);
}
.settings__column--dragging {
  opacity: 0.5;
}
.settings__grip {
  display: inline-flex;
  color: var(--text-secondary);
  cursor: grab;
}
.settings__buttons {
  display: inline-flex;
  gap: 2px;
  align-self: center;
}
.settings__icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--tertiary-label);
  cursor: pointer;
}
.settings__icon:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--text-primary);
}
.settings__icon:disabled {
  opacity: 0.35;
  cursor: default;
}
.settings__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
@media (max-width: 560px) {
  .settings__sort {
    grid-template-columns: 24px minmax(0, 1fr) auto;
  }
  .settings__sort > :nth-child(3) {
    grid-column: 2;
  }
}
</style>
